import { useState, useEffect, useReducer, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faLink,
  faClockRotateLeft,
  faSearch,
  faDatabase,
  faFile,
  faPen,
  faTrash,
  faCheck,
  faXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/context/WebSocketContext";
import { ErrorBanner } from "@/components/ErrorBanner";
import { Badge, colors as badgeColors } from "@/components/ui/badge/Badge";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import Drawer from "@/components/ui/drawer";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";
import { AgenticAPI } from "@/services/api";
import {
  EmbeddablePDFViewer,
  type PDFHighlight,
} from "@/components/pdf/EmbeddablePDFViewer";
import { useDebounce } from "@/hooks/useDebounce";
import clsx from "clsx";

interface DataHistoryItem {
  value: string;
  timestamp: string;
  actor: string;
  type: string;
  confidence: number;
  source_file:
  | string
  | {
    label: string;
    file_id: string;
  };
  source_reference?: string;
}

export interface DataItem {
  id: string;
  key: string;
  value: string | object;
  source_file:
  | string
  | {
    label: string;
    file_id: string;
  };
  source_reference?: string;
  confidence?: number;
  history: DataHistoryItem[];
  updatedAt: string;
  label?: string;
}

export interface DataTableSummary {
  id: string;
  name: string;
  fieldCount: number;
  filledFieldCount: number;
  createdAt?: string;
  summary?: string;
}

export interface DataTable extends DataTableSummary {
  data: DataItem[];
}

interface DataProps {
  entityId: string;
  entityType: string;
  fetchSummaries: (entityId: string) => Promise<DataTableSummary[]>;
  fetchDataset: (datasetId: string) => Promise<DataTable>;
  updateField?: (datasetId: string, fieldName: string, newValue: string, updatedBy: string, note?: string) => Promise<DataTable>;
  deleteDataset?: (datasetId: string) => Promise<void>;
  websocketMessageType?: string;
}

// Define State and Action types for reducer
type DataState = {
  summaries: DataTableSummary[];
  loading: boolean;
  error: string | null;
};

type DataAction =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; payload: { summaries: DataTableSummary[] } }
  | { type: "FETCH_ERROR"; payload: string }
  | { type: "ADD_SUMMARY"; payload: DataTableSummary }
  | { type: "REMOVE_SUMMARY"; payload: string };

// Reducer function
function dataReducer(state: DataState, action: DataAction): DataState {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return {
        ...state,
        loading: false,
        summaries: action.payload.summaries,
      };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "ADD_SUMMARY":
      if (state.summaries.some((s) => s.id === action.payload.id)) {
        return state;
      }
      return {
        ...state,
        summaries: [action.payload, ...state.summaries],
      };
    case "REMOVE_SUMMARY":
      return {
        ...state,
        summaries: state.summaries.filter((s) => s.id !== action.payload),
      };
    default:
      return state;
  }
}

// Helper function for confidence color
const getConfidenceColor = (
  score: number | undefined
): keyof typeof badgeColors => {
  if (score === undefined) return "zinc";
  if (score < 0.5) return "red";
  if (score < 0.8) return "amber";
  return "green";
};

// Format relative date
const formatRelativeDate = (dateString?: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

export function Data({
  entityId,
  entityType,
  fetchSummaries,
  fetchDataset,
  updateField,
  deleteDataset,
  websocketMessageType,
}: DataProps) {
  const { lastMessage, subscribe, unsubscribe } = useWebSocket();
  const { user } = useAuth();
  const initialState: DataState = {
    summaries: [],
    loading: true,
    error: null,
  };
  const [state, dispatch] = useReducer(dataReducer, initialState);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [drawerSearch, setDrawerSearch] = useState("");
  const debouncedDrawerSearch = useDebounce(drawerSearch, 300);
  const [selectedSummary, setSelectedSummary] = useState<DataTableSummary | null>(null);
  const [selectedDataset, setSelectedDataset] = useState<DataTable | null>(null);
  const [datasetLoading, setDatasetLoading] = useState(false);
  const [historyModalItem, setHistoryModalItem] = useState<DataItem | null>(
    null
  );

  // Edit field state
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // PDF Viewer state
  const [pdfViewerState, setPdfViewerState] = useState<{
    isOpen: boolean;
    pdfUrl: string | null;
    pageNumber: number;
    highlights: PDFHighlight[];
    sourceItem: DataItem | null;
    isBlobUrl: boolean;
  }>({
    isOpen: false,
    pdfUrl: null,
    pageNumber: 1,
    highlights: [],
    sourceItem: null,
    isBlobUrl: false,
  });

  // Cleanup blob URL when dialog closes
  const closePdfViewer = useCallback(() => {
    if (pdfViewerState.isBlobUrl && pdfViewerState.pdfUrl) {
      URL.revokeObjectURL(pdfViewerState.pdfUrl);
    }
    setPdfViewerState({
      isOpen: false,
      pdfUrl: null,
      pageNumber: 1,
      highlights: [],
      sourceItem: null,
      isBlobUrl: false,
    });
  }, [pdfViewerState.isBlobUrl, pdfViewerState.pdfUrl]);

  // Fetch initial summaries
  const loadData = useCallback(async () => {
    dispatch({ type: "FETCH_START" });
    try {
      const fetchedSummaries = await fetchSummaries(entityId);
      dispatch({ type: "FETCH_SUCCESS", payload: { summaries: fetchedSummaries } });
    } catch (err) {
      dispatch({
        type: "FETCH_ERROR",
        payload: err instanceof Error ? err.message : "Failed to load data",
      });
    }
  }, [entityId, fetchSummaries]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fetch full dataset when a summary is selected
  const handleSelectSummary = useCallback(async (summary: DataTableSummary) => {
    setSelectedSummary(summary);
    setSelectedDataset(null);
    setDatasetLoading(true);
    try {
      const fullDataset = await fetchDataset(summary.id);
      setSelectedDataset(fullDataset);
    } catch (err) {
      console.error("Failed to fetch dataset:", err);
    } finally {
      setDatasetLoading(false);
    }
  }, [fetchDataset]);

  // Close drawer
  const handleCloseDrawer = useCallback(() => {
    setSelectedSummary(null);
    setSelectedDataset(null);
    setDrawerSearch("");
    setEditingField(null);
  }, []);

  // Filter summaries by search
  const filteredSummaries = useMemo(() => {
    if (!debouncedSearch) return state.summaries;
    const searchLower = debouncedSearch.toLowerCase();
    return state.summaries.filter((summary) =>
      summary.name.toLowerCase().includes(searchLower)
    );
  }, [state.summaries, debouncedSearch]);

  // Filter fields in drawer by search
  const filteredFields = useMemo(() => {
    if (!selectedDataset?.data) return [];
    if (!debouncedDrawerSearch) return selectedDataset.data;
    const searchLower = debouncedDrawerSearch.toLowerCase();
    return selectedDataset.data.filter((item) => {
      const labelMatch = (item.label || item.key).toLowerCase().includes(searchLower);
      const valueMatch = typeof item.value === "string" && item.value.toLowerCase().includes(searchLower);
      return labelMatch || valueMatch;
    });
  }, [selectedDataset?.data, debouncedDrawerSearch]);

  // Start editing a field
  const handleStartEdit = useCallback((item: DataItem) => {
    setEditingField(item.key);
    setEditValue(typeof item.value === "string" ? item.value : JSON.stringify(item.value));
  }, []);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingField(null);
    setEditValue("");
    setEditNote("");
  }, []);

  // Save edited field
  const handleSaveEdit = useCallback(async (item: DataItem) => {
    if (!updateField || !selectedSummary || !user) return;

    setEditSaving(true);
    try {
      const updatedDataset = await updateField(
        selectedSummary.id,
        item.key,
        editValue,
        user.name || user.email || "Unknown User",
        editNote || undefined
      );
      setSelectedDataset(updatedDataset);
      setEditingField(null);
      setEditValue("");
      setEditNote("");
    } catch (err) {
      console.error("Failed to save field:", err);
    } finally {
      setEditSaving(false);
    }
  }, [updateField, selectedSummary, user, editValue, editNote]);

  // Delete dataset
  const handleDeleteDataset = useCallback(async () => {
    if (!deleteDataset || !selectedSummary) return;

    setDeleteLoading(true);
    try {
      await deleteDataset(selectedSummary.id);
      dispatch({ type: "REMOVE_SUMMARY", payload: selectedSummary.id });
      handleCloseDrawer();
      setDeleteConfirmOpen(false);
    } catch (err) {
      console.error("Failed to delete dataset:", err);
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteDataset, selectedSummary, handleCloseDrawer]);

  // Handle viewing source document
  const handleViewSource = useCallback(
    async (item: DataItem) => {
      try {
        const sourceFileName =
          typeof item.source_file === "string"
            ? item.source_file
            : item.source_file?.label;

        if (!sourceFileName) {
          console.warn("No source file name available");
          return;
        }

        const documents = await AgenticAPI.getAgenticDocuments(entityId);
        const matchingDoc = Array.isArray(documents)
          ? documents.find(
            (doc: any) =>
              doc.display_name === sourceFileName ||
              doc.name === sourceFileName ||
              doc.original_filename === sourceFileName
          )
          : null;

        if (!matchingDoc) {
          console.warn("Could not find document for:", sourceFileName);
          return;
        }

        const documentId =
          (matchingDoc as any)._id ||
          (matchingDoc as any).document_id ||
          (matchingDoc as any).id ||
          (matchingDoc as any).file_id;

        if (!documentId) {
          console.warn("Document found but no document_id available");
          return;
        }

        let pdfUrl: string;
        let isBlobUrl = false;

        try {
          const pdfBlob = await AgenticAPI.downloadAgenticDocument(documentId);
          pdfUrl = URL.createObjectURL(pdfBlob);
          isBlobUrl = true;
        } catch (downloadError) {
          console.error("Failed to download PDF:", downloadError);
          pdfUrl = (matchingDoc as any).url;
          if (!pdfUrl) {
            console.error("No URL available for PDF");
            return;
          }
        }

        let pageNumber = 1;
        if (item.source_reference) {
          const pageMatch = item.source_reference.match(/page\s+(\d+)/i);
          if (pageMatch) {
            pageNumber = parseInt(pageMatch[1], 10);
          }
        }

        const highlight: PDFHighlight = {
          id: item.id,
          page: pageNumber,
          boundingBox: {
            x: 10,
            y: 20,
            width: 80,
            height: 10,
          },
          color: "rgba(59, 130, 246, 0.3)",
          label: item.label || item.key,
          sourceText: typeof item.value === "string" ? item.value : undefined,
        };

        setPdfViewerState({
          isOpen: true,
          pdfUrl: pdfUrl,
          pageNumber,
          highlights: [highlight],
          sourceItem: item,
          isBlobUrl,
        });
      } catch (error) {
        console.error("Error loading source document:", error);
      }
    },
    [entityId]
  );

  // WebSocket Subscription
  useEffect(() => {
    if (websocketMessageType) {
      subscribe(websocketMessageType);
      return () => {
        unsubscribe(websocketMessageType);
      };
    }
  }, [websocketMessageType, subscribe, unsubscribe]);

  // WebSocket Message Handling
  useEffect(() => {
    if (!websocketMessageType) return;
    const message: any = lastMessage.get(websocketMessageType);

    if (
      message &&
      message.full_document &&
      message.full_document.pod_instance_id === entityId
    ) {
      if (message.operation === "insert") {
        const fullDoc = message.full_document;
        const data = fullDoc.data || [];
        const filledCount = data.filter((field: any) => {
          if (field.value === null || field.value === undefined) return false;
          if (typeof field.value === "string") return field.value.trim() !== "";
          return true;
        }).length;

        const newSummary: DataTableSummary = {
          id: fullDoc._id,
          name: fullDoc.name,
          createdAt: fullDoc.created_at || new Date().toISOString(),
          summary: fullDoc.summary,
          fieldCount: data.length,
          filledFieldCount: filledCount,
        };
        dispatch({ type: "ADD_SUMMARY", payload: newSummary });
      }
    }
  }, [lastMessage, entityId, websocketMessageType]);

  if (state.loading) {
    return (
      <div className="p-4 flex h-full w-full items-center justify-center font-mono text-slate-600 dark:text-slate-400">
        Loading data...
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="p-4 flex h-full w-full">
        <ErrorBanner message={`Error: ${state.error}`} />
      </div>
    );
  }

  if (state.summaries.length === 0) {
    return (
      <div className="p-8 w-full h-full flex flex-col items-center justify-center text-center text-slate-500 dark:text-slate-400">
        <FontAwesomeIcon icon={faDatabase} className="text-4xl mb-4 opacity-20" />
        <span className="text-slate-700 dark:text-slate-300">No data extracted yet</span>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Header with search */}
      <div className="px-6 pt-6 pb-4 shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {filteredSummaries.length} dataset{filteredSummaries.length !== 1 ? "s" : ""}
          </div>
        </div>
        <FormInput
          placeholder="Search datasets..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<FontAwesomeIcon icon={faSearch} />}
          iconPosition="start"
          canClear={true}
          showClearButton={true}
          onClear={() => setSearch("")}
        />
      </div>

      {/* Dataset List */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl overflow-hidden">
          {filteredSummaries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <FontAwesomeIcon icon={faSearch} className="text-2xl mb-3 opacity-30" />
              <span className="text-sm">No datasets match your search</span>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
              {filteredSummaries.map((summary) => (
                <div
                  key={summary.id}
                  onClick={() => handleSelectSummary(summary)}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer group"
                >
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center shrink-0">
                    <FontAwesomeIcon
                      icon={faFile}
                      className="text-slate-400 dark:text-slate-500"
                    />
                  </div>

                  {/* Name & meta */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 dark:text-white truncate">
                      {summary.name}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-3">
                      <span>{summary.fieldCount} field{summary.fieldCount !== 1 ? "s" : ""}</span>
                      {summary.createdAt && (
                        <span>{formatRelativeDate(summary.createdAt)}</span>
                      )}
                    </div>
                    {/* Progress bar */}
                    {(() => {
                      const completeness = summary.fieldCount > 0
                        ? (summary.filledFieldCount / summary.fieldCount) * 100
                        : 0;
                      return (
                        <div className="mt-2 h-1.5 w-28 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={clsx(
                              "h-full rounded-full transition-all",
                              completeness === 100
                                ? "bg-emerald-400"
                                : completeness >= 80
                                  ? "bg-emerald-400/70"
                                  : "bg-amber-400"
                            )}
                            style={{ width: `${completeness}%` }}
                          />
                        </div>
                      );
                    })()}
                  </div>

                  {/* Chevron */}
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-slate-300 dark:text-slate-600 group-hover:text-indigo-500 group-hover:bg-white dark:group-hover:bg-slate-700 group-hover:shadow-sm transition-all opacity-0 group-hover:opacity-100 shrink-0">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dataset Detail Drawer */}
      <Drawer
        isOpen={selectedSummary !== null}
        onClose={handleCloseDrawer}
        title={selectedSummary?.name || "Dataset"}
        width="max-w-2xl"
      >
        <div className="flex flex-col h-full">
          <div className="px-6 pb-6 overflow-y-auto flex-1">
            {datasetLoading ? (
              <div className="flex items-center justify-center py-12 text-slate-600 dark:text-slate-400">
                <span className="text-sm">Loading dataset...</span>
              </div>
            ) : selectedDataset ? (
              <div className="space-y-4 pt-4">
                {/* Summary section */}
                {selectedDataset.summary && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-700/30 rounded-xl border-l-4 border-indigo-400">
                    <div className="text-xs font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wide mb-2">
                      Summary
                    </div>
                    <div className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                      {selectedDataset.summary}
                    </div>
                  </div>
                )}

                {/* Progress bar */}
                {(() => {
                  const totalFields = selectedDataset.data.length;
                  const filledFields = selectedDataset.data.filter((item) => {
                    if (typeof item.value === "string") return item.value.trim() !== "";
                    return item.value !== null && item.value !== undefined;
                  }).length;
                  const completeness = totalFields > 0 ? (filledFields / totalFields) * 100 : 0;

                  return (
                    <div className="flex items-center gap-3 py-2">
                      <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={clsx(
                            "h-full rounded-full transition-all",
                            completeness === 100
                              ? "bg-emerald-400"
                              : completeness >= 80
                                ? "bg-emerald-400/70"
                                : "bg-amber-400"
                          )}
                          style={{ width: `${completeness}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">
                        {filledFields}/{totalFields} fields
                      </span>
                    </div>
                  );
                })()}

                {/* Search fields */}
                <FormInput
                  placeholder="Search fields..."
                  value={drawerSearch}
                  onChange={(e) => setDrawerSearch(e.target.value)}
                  icon={<FontAwesomeIcon icon={faSearch} />}
                  iconPosition="start"
                  canClear={true}
                  showClearButton={true}
                  onClear={() => setDrawerSearch("")}
                />

                {/* Fields list */}
                <div className="space-y-1">
                  {filteredFields.length === 0 && debouncedDrawerSearch ? (
                    <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                      <FontAwesomeIcon icon={faSearch} className="text-xl mb-2 opacity-30" />
                      <span className="text-sm">No fields match your search</span>
                    </div>
                  ) : (
                    filteredFields.map((item) => {
                      const confidence =
                        item.confidence !== undefined
                          ? item.confidence
                          : item.history?.[0]?.confidence;

                      const contentValue =
                        typeof item.value === "object"
                          ? JSON.stringify(item.value, null, 2)
                          : String(item.value);

                      const isEmpty =
                        typeof item.value === "string"
                          ? item.value.trim() === ""
                          : item.value === null || item.value === undefined;

                      // Get source info
                      const sourceLabel =
                        typeof item.source_file === "string"
                          ? item.source_file
                          : item.source_file?.label;
                      const sourceReference = item.source_reference;

                      const isEditing = editingField === item.key;

                      return (
                        <div
                          key={item.id}
                          className={clsx(
                            "flex gap-4 p-3 rounded-xl transition-colors group/field",
                            isEmpty
                              ? "opacity-50"
                              : "hover:bg-slate-50 dark:hover:bg-slate-700/30"
                          )}
                        >
                          {/* Label */}
                          <div className="w-1/3 shrink-0 text-sm text-slate-500 dark:text-slate-400 text-right pt-0.5">
                            {item.label || item.key}
                          </div>

                          {/* Value & meta */}
                          <div className="flex-1 min-w-0">
                            {isEditing ? (
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                                    Value
                                  </label>
                                  <textarea
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                    rows={3}
                                    autoFocus
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                                    Reference / Note <span className="text-slate-400">(optional)</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={editNote}
                                    onChange={(e) => setEditNote(e.target.value)}
                                    placeholder="e.g., Verified via client phone call"
                                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    color="indigo"
                                    className="text-sm h-8"
                                    onClick={() => handleSaveEdit(item)}
                                    disabled={editSaving}
                                  >
                                    <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                    {editSaving ? "Saving..." : "Save"}
                                  </Button>
                                  <Button
                                    plain
                                    className="text-sm h-8"
                                    onClick={handleCancelEdit}
                                    disabled={editSaving}
                                  >
                                    <FontAwesomeIcon icon={faXmark} className="mr-1" />
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start gap-2">
                                <div className="flex-1 text-sm text-slate-900 dark:text-white break-words">
                                  {isEmpty ? (
                                    <span className="italic text-slate-400">Not found</span>
                                  ) : (
                                    contentValue
                                  )}
                                </div>
                                {/* Edit button */}
                                {updateField && !isEmpty && (
                                  <button
                                    onClick={() => handleStartEdit(item)}
                                    className="opacity-0 group-hover/field:opacity-100 p-1.5 rounded-md text-slate-400 hover:text-indigo-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shrink-0"
                                    title="Edit field"
                                  >
                                    <FontAwesomeIcon icon={faPen} className="text-xs" />
                                  </button>
                                )}
                              </div>
                            )}

                            {!isEditing && !isEmpty && (
                              <div className="mt-2 space-y-1.5">
                                {/* Source file & reference */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewSource(item);
                                  }}
                                  className="flex items-start gap-1.5 text-xs text-slate-400 hover:text-indigo-500 transition-colors text-left"
                                >
                                  <FontAwesomeIcon icon={faLink} className="text-[10px] mt-0.5 shrink-0" />
                                  <div className="flex flex-col gap-0.5">
                                    {sourceLabel && (
                                      <span className="font-medium">{sourceLabel}</span>
                                    )}
                                    {sourceReference && (
                                      <span className="text-slate-500 dark:text-slate-500">
                                        {sourceReference}
                                      </span>
                                    )}
                                  </div>
                                </button>

                                {/* Confidence & History */}
                                <div className="flex items-center gap-3">
                                  {confidence !== undefined && (
                                    <span
                                      className={clsx(
                                        "text-[10px] font-medium",
                                        confidence >= 0.8
                                          ? "text-emerald-500 dark:text-emerald-400"
                                          : confidence >= 0.5
                                            ? "text-amber-500 dark:text-amber-400"
                                            : "text-red-500 dark:text-red-400"
                                      )}
                                    >
                                      {confidence >= 0.8
                                        ? "High confidence"
                                        : confidence >= 0.5
                                          ? "Medium confidence"
                                          : "Low confidence"}
                                    </span>
                                  )}

                                  {item.history && item.history.length > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setHistoryModalItem(item);
                                      }}
                                      className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-indigo-500 transition-colors"
                                    >
                                      <FontAwesomeIcon icon={faClockRotateLeft} className="text-[10px]" />
                                      <span>History</span>
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Delete button at bottom */}
                {deleteDataset && (
                  <div className="pt-6 mt-4 border-t border-slate-100 dark:border-slate-700/50">
                    <button
                      onClick={() => setDeleteConfirmOpen(true)}
                      className="flex items-center gap-2 text-sm text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-xs" />
                      Delete this dataset
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        size="sm"
      >
        <DialogTitle>Delete Dataset</DialogTitle>
        <DialogBody>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Are you sure you want to delete <strong>{selectedSummary?.name}</strong>?
            This action can be undone by an administrator.
          </p>
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDeleteDataset}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Modal */}
      <Dialog
        open={historyModalItem !== null}
        onClose={() => setHistoryModalItem(null)}
        size="lg"
      >
        <DialogTitle>History — {historyModalItem?.label}</DialogTitle>
        <DialogBody>
          {historyModalItem?.history && historyModalItem.history.length > 0 ? (
            <div className="space-y-4">
              {/* Current Value */}
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-400">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-green-800 dark:text-green-200 text-sm">
                      Current
                    </div>
                    <div className="text-slate-900 dark:text-slate-100 mt-1 text-sm">
                      {typeof historyModalItem.value === "object"
                        ? JSON.stringify(historyModalItem.value)
                        : String(historyModalItem.value) || "(empty)"}
                    </div>
                  </div>
                  {historyModalItem.confidence !== undefined && (
                    <Badge color={getConfidenceColor(historyModalItem.confidence)}>
                      {Math.round(historyModalItem.confidence * 100)}%
                    </Badge>
                  )}
                </div>
              </div>

              {/* History Entries */}
              {historyModalItem.history
                .slice()
                .sort(
                  (a, b) =>
                    new Date(b.timestamp).getTime() -
                    new Date(a.timestamp).getTime()
                )
                .map((entry, index) => (
                  <div
                    key={index}
                    className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {new Date(entry.timestamp).toLocaleString()} • {entry.actor} • {entry.type}
                      </div>
                      {entry.confidence !== undefined && entry.confidence !== null && (
                        <Badge color={getConfidenceColor(entry.confidence)}>
                          {Math.round(entry.confidence * 100)}%
                        </Badge>
                      )}
                    </div>
                    <div className="text-slate-900 dark:text-slate-100 text-sm">
                      {entry.value || "(empty)"}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-slate-500 dark:text-slate-400">
              No history available for this field.
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button onClick={() => setHistoryModalItem(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* PDF Viewer Dialog */}
      <Dialog open={pdfViewerState.isOpen} onClose={closePdfViewer} size="5xl">
        <DialogTitle>
          Source Document
          {pdfViewerState.sourceItem && (
            <span className="text-sm font-normal text-slate-500 dark:text-slate-400 ml-2">
              — {pdfViewerState.sourceItem.label || pdfViewerState.sourceItem.key}
            </span>
          )}
        </DialogTitle>
        <DialogBody>
          <div className="h-[70vh]">
            {pdfViewerState.pdfUrl && (
              <EmbeddablePDFViewer
                pdfUrl={pdfViewerState.pdfUrl}
                height="100%"
                initialPage={pdfViewerState.pageNumber}
                showAnnotations={true}
                highlights={pdfViewerState.highlights}
              />
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button onClick={closePdfViewer}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
